
import type { Facet } from '@empathybroker/tsclient-play/dist/models/facet'
import type { FacetValue } from '@empathybroker/tsclient-play/dist/models/facet-value'
import type { PropType } from 'vue'
import { RankingActionEnum } from '@empathybroker/tsclient-play/dist/enums/ranking-action.enum'
import { defineComponent } from 'vue'
import { ColorEnum } from '../../enums'

import { capitalizeSentence } from '../../filters'

import EditableLabel from '../editable-label.component.vue'
import { UiSelect } from '../keen-ui'
import MutableList from '../mutable-list.component.vue'
import { EpbBadge } from '../shared/epb-badge'
import { EpbButton } from '../shared/epb-button'

export default defineComponent({
  name: 'FacetsConfigurationList',
  components: {
    EditableLabel,
    EpbBadge,
    EpbButton,
    MutableList,
    UiSelect,
  },
  props: {
    facets: {
      type: Array as PropType<Array<Facet>>,
      required: true,
    },
    sortTypeOptions: {
      type: Array as PropType<Array<{ value: string; label: string }>>,
      required: true,
    },
    readOnly: Boolean,
    messages: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      editFormValues: {},
      expandedFacetIds: [],
    }
  },
  computed: {
    sortTypeSelectOptions(): any[] {
      return this.sortTypeOptions.map(option => ({
        ...option,
        label: capitalizeSentence(`${this.messages.sortType.inlineLabel} ${option.label}`),
      }))
    },
  },
  methods: {
    onMoveFacet({ item: facet, targetIndex }): void {
      this.facets.splice(this.facets.indexOf(facet), 1)
      this.facets.splice(targetIndex, 0, facet)
    },
    onEditFacet(facet: Facet): void {
      if (!this.isFacetIdEdited(facet.id)) {
        this.editFormValues = {
          id: facet.id,
          prettyName: facet.prettyName,
          sortType: this.getFacetSortTypeOption(facet),
        }
      } else {
        Object.assign(facet, {
          prettyName: this.editFormValues.prettyName,
          sortType: this.editFormValues.sortType.value,
        })
        this.editFormValues = {}
      }
    },
    isFacetIdEdited(facetId: string): boolean {
      return facetId === this.editFormValues.id
    },
    getFacetSortTypeOption(facet: Facet): any {
      return this.sortTypeSelectOptions.find(option => option.value === facet.sortType)
    },
    onExpandFacet(facet: Facet): void {
      const indexOfExpandedFacetId = this.expandedFacetIds.indexOf(facet.id)
      if (indexOfExpandedFacetId < 0) {
        this.expandedFacetIds.push(facet.id)
      } else {
        this.expandedFacetIds.splice(indexOfExpandedFacetId, 1)
      }
    },
    onRemoveFacet(facet: Facet): void {
      this.facets.splice(this.facets.indexOf(facet), 1)
    },
    getFacetPrettyNameValue(facet: Facet): string {
      return this.isFacetIdEdited(facet.id)
        ? this.editFormValues.prettyName
        : capitalizeSentence(facet.prettyName || facet.id)
    },
    getFacetSortTypeValue(facet: Facet): string {
      return this.isFacetIdEdited(facet.id)
        ? this.editFormValues.sortType
        : this.getFacetSortTypeOption(facet)
    },
    getSortedFacetValuesByAction(facetValues: FacetValue[]): FacetValue[] {
      return [...facetValues].sort((facetValue1: FacetValue, facetValue2: FacetValue) => {
        if (facetValue1.action !== facetValue2.action) {
          return facetValue1.action === RankingActionEnum.BOOST ? -1 : 1
        }
        return 0
      })
    },
    getActionColor(action: RankingActionEnum): ColorEnum {
      return {
        [RankingActionEnum.BOOST]: ColorEnum.GREEN_75,
        [RankingActionEnum.BURY]: ColorEnum.RED_75,
      }[action]
    },
    onRemoveFacetValue(facet: Facet, facetValue: FacetValue): void {
      facet.values.splice(facet.values.indexOf(facetValue), 1)
    },
  },
})
